import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Pause, Play, Plus, Split, Trash2, X, Zap } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  InfoHint,
  Input,
  Modal,
  Select,
  Textarea,
  toast,
} from '../../components/ui';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { formatDate, formatMoney } from '../../lib/format';
import { useInvoiceClients } from './api';
import { ScheduleStripePanel } from './stripe-link';
import {
  CADENCE_OPTIONS,
  useCreateRecurring,
  useDeleteRecurring,
  useGenerateRecurring,
  useLinkPastRecurring,
  useSubjectPreview,
  useUpdateRecurring,
  type Cadence,
  type LinkPastResult,
  type RecurringTemplate,
} from './recurring-api';

/**
 * The whole form is the sheet's four columns — client, subject, amount, next
 * invoice date — plus one pre-filled dropdown for the cadence, because the
 * sheet prints it in the same cell ("07/01/2026 (every month)").
 *
 * Everything else lives under "Optional detail" and has a working default:
 * net 30, kind `recurring`, no note. A field you must fill in is a
 * step you have added; five of those is a form nobody opens seventeen times.
 */

const KIND_OPTIONS = [
  { value: 'recurring', label: 'Recurring' },
  { value: 'support', label: 'Support' },
  { value: 'service', label: 'Service' },
  { value: 'other', label: 'Other' },
];

/** One line of the schedule. A schedule with ONE line is the ordinary case:
 *  its amount and agreement are the two fields the form always showed. */
interface LineState {
  description: string;
  amount: string;
  agreementId: string;
}

const emptyLine = (): LineState => ({
  description: '',
  amount: '',
  agreementId: '',
});

interface FormState {
  clientId: string;
  subject: string;
  /** Always at least one. Two or more = split across agreements. */
  lines: LineState[];
  nextIssueDate: string;
  cadence: Cadence;
  kind: string;
  netDays: string;
  note: string;
  autoSend: boolean;
}

function blank(today: string): FormState {
  return {
    clientId: '',
    subject: '',
    lines: [emptyLine()],
    // The 1st of next month is what a monthly retainer almost always wants.
    nextIssueDate: firstOfNextMonth(today),
    cadence: 'monthly',
    kind: 'recurring',
    netDays: '30',
    note: '',
    autoSend: false,
  };
}

function firstOfNextMonth(today: string): string {
  const [y, m] = today.split('-').map(Number);
  const year = m === 12 ? y + 1 : y;
  const month = m === 12 ? 1 : m + 1;
  return `${year}-${String(month).padStart(2, '0')}-01`;
}

function toFormState(t: RecurringTemplate): FormState {
  return {
    clientId: t.clientId,
    subject: t.subject,
    lines:
      t.lines.length > 1
        ? t.lines.map((l) => ({
            description: l.description ?? '',
            amount: String(l.amount),
            agreementId: l.agreementId ?? '',
          }))
        : [
            {
              description: '',
              amount: String(t.amount),
              agreementId: t.agreementId ?? '',
            },
          ],
    nextIssueDate: t.nextIssueDate,
    cadence: t.cadence,
    kind: t.kind,
    netDays: String(t.netDays),
    note: t.note ?? '',
    autoSend: t.autoSend,
  };
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** Active agreements for the chosen client — only fetched once one is chosen. */
function useClientAgreements(clientId: string) {
  return useQuery({
    queryKey: ['agreements', 'options', clientId],
    queryFn: () =>
      api.get<{ agreements: Array<{ id: string; name: string }> }>('/agreements', {
        params: { clientId, active: 'true' },
      }),
    enabled: Boolean(clientId),
    staleTime: 5 * 60_000,
  });
}

export function RecurringFormModal({
  open,
  template,
  today,
  onOpenChange,
}: {
  open: boolean;
  /** null = create a new schedule. */
  template: RecurringTemplate | null;
  today: string;
  onOpenChange: (open: boolean) => void;
}) {
  const clients = useInvoiceClients();
  const create = useCreateRecurring();
  const update = useUpdateRecurring();
  const remove = useDeleteRecurring();
  const generate = useGenerateRecurring();
  const linkPast = useLinkPastRecurring();
  /** After a save: the schedule's past invoices that could be credited now. */
  const [pastPlan, setPastPlan] = useState<{
    id: string;
    result: LinkPastResult;
  } | null>(null);

  const [form, setForm] = useState<FormState>(() => blank(today));
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [showDetail, setShowDetail] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(template ? toFormState(template) : blank(today));
    setErrors({});
    setShowDetail(false);
  }, [open, template, today]);

  const agreements = useClientAgreements(form.clientId);
  const netDaysNum = Number(form.netDays) || 30;
  // Debounced: the preview is resolved server-side (one resolver, not two), and
  // a request per keystroke would be forty requests to type one subject.
  const debouncedSubject = useDebounced(form.subject, 250);
  const preview = useSubjectPreview(debouncedSubject, form.nextIssueDate, netDaysNum);

  const clientOptions = useMemo(
    () => (clients.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [clients.data],
  );
  const agreementOptions = useMemo(
    () => [
      { value: '', label: 'No agreement' },
      ...(agreements.data?.agreements ?? []).map((p) => ({
        value: p.id,
        label: p.name,
      })),
    ],
    [agreements.data],
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const multi = form.lines.length > 1;
  const lineAmounts = form.lines.map((l) => Number(l.amount));
  const amountNum =
    lineAmounts.reduce((s, n) => s + (Number.isFinite(n) ? Math.round(n * 100) : 0), 0) / 100;
  const pending = create.isPending || update.isPending;
  const setLine = (i: number, patch: Partial<LineState>) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)),
    }));
    setErrors((prev) => ({ ...prev, lines: undefined }));
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!form.clientId) next.clientId = 'Pick a client';
    if (!form.subject.trim()) next.subject = 'What is this invoice for?';
    const badLine = lineAmounts.findIndex((n) => !Number.isFinite(n) || n <= 0);
    if (badLine >= 0) {
      next.lines = multi
        ? `Line ${badLine + 1} needs an amount above zero`
        : 'Enter an amount above zero';
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.nextIssueDate)) next.nextIssueDate = 'Pick a date';
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    const payload = {
      clientId: form.clientId,
      subject: form.subject.trim(),
      amount: amountNum,
      nextIssueDate: form.nextIssueDate,
      cadence: form.cadence,
      ...(multi
        ? {
            lines: form.lines.map((l) => ({
              description: l.description.trim() || null,
              amount: Number(l.amount),
              agreementId: l.agreementId || null,
            })),
          }
        : { agreementId: form.lines[0].agreementId || null, lines: [] }),
      kind: form.kind,
      netDays: netDaysNum,
      note: form.note.trim() || null,
      autoSend: form.autoSend,
    };

    const done = (message: string) => () => {
      toast.success(message);
      onOpenChange(false);
    };
    const failed = (fallback: string) => (error: unknown) =>
      toast.error(error instanceof Error ? error.message : fallback);

    if (template) {
      update.mutate(
        { id: template.id, ...payload },
        {
          onSuccess: () => {
            done('Schedule updated')();
            // Offer to credit the invoices this schedule already made — a
            // question only when there is something to do.
            linkPast.mutate(
              { id: template.id, apply: false },
              {
                onSuccess: (result) => {
                  if (result.linked.length + result.split.length > 0) {
                    setPastPlan({ id: template.id, result });
                  }
                },
              },
            );
          },
          onError: failed('Could not update the schedule'),
        },
      );
    } else {
      create.mutate(payload, {
        onSuccess: done('Schedule created'),
        onError: failed('Could not create the schedule'),
      });
    }
  };

  const unknown = preview.data?.unknownVariables ?? [];

  return (
    <>
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title={template ? 'Edit recurring schedule' : 'New recurring schedule'}
        size="lg"
        description={
          template?.stripe
            ? undefined // the Stripe panel below says it
            : form.autoSend
              ? 'Issues and emails the invoice on schedule.'
              : 'Drafts the invoice on schedule; nothing is sent until you send it.'
        }
      >
        <form onSubmit={submit} className="space-y-3.5">
          {/* The row's two actions, repeated here because below 640px the
              table hides its action column and this modal IS the row. */}
          {template && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-grey-200 bg-grey-50 px-3 py-2">
              <span className="text-caption text-grey-600">
                Next invoice{' '}
                <span className="num font-semibold text-secondary">
                  {formatDate(template.nextIssueDate)}
                </span>
                {template.lastIssuedAt && (
                  <> · last generated {formatDate(template.lastIssuedAt.slice(0, 10))}</>
                )}
              </span>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  loading={generate.isPending}
                  onClick={() =>
                    generate.mutate(template.id, {
                      onSuccess: (invoice) => toast.success(`Draft #${invoice.number} created`),
                      onError: (error) =>
                        toast.error(
                          error instanceof Error ? error.message : 'Could not generate the draft',
                        ),
                    })
                  }
                >
                  <Zap className="h-4 w-4" aria-hidden />
                  Generate now
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    update.mutate(
                      { id: template.id, isActive: !template.isActive },
                      {
                        onSuccess: () =>
                          toast.success(template.isActive ? 'Schedule paused' : 'Schedule resumed'),
                        onError: (error) =>
                          toast.error(
                            error instanceof Error
                              ? error.message
                              : 'Could not update the schedule',
                          ),
                      },
                    )
                  }
                >
                  {template.isActive ? (
                    <Pause className="h-4 w-4" aria-hidden />
                  ) : (
                    <Play className="h-4 w-4" aria-hidden />
                  )}
                  {template.isActive ? 'Pause' : 'Resume'}
                </Button>
              </div>
            </div>
          )}

          {template && <ScheduleStripePanel template={template} />}

          <div className={cn('grid gap-3', !multi && 'sm:grid-cols-2')}>
            <Select
              label="Client"
              options={clientOptions}
              placeholder={clients.isLoading ? 'Loading clients…' : 'Choose a client'}
              value={form.clientId}
              onChange={(e) => {
                set('clientId', e.target.value);
                // An agreement belongs to one client; keeping the old one would
                // silently bill the wrong agreement.
                setForm((prev) => ({
                  ...prev,
                  lines: prev.lines.map((l) => ({ ...l, agreementId: '' })),
                }));
              }}
              error={errors.clientId}
            />

            {/* Out of "Optional detail" (Jesse, 2026-11-04): every invoice this
              schedule makes carries the agreement on its line, which is what
              counts it as invoiced ON the agreement. Hidden, it defaulted to
              nothing and monthly retainers read "$0 invoiced" on /agreements. */}
            {!multi && (
              <div>
                <Select
                  label="Agreement"
                  options={agreementOptions}
                  value={form.lines[0].agreementId}
                  disabled={!form.clientId}
                  onChange={(e) => setLine(0, { agreementId: e.target.value })}
                />
                {form.clientId && !form.lines[0].agreementId && (
                  <p className="mt-1.5 text-caption text-status-partial">
                    With no agreement, these invoices are not counted on any agreement.
                  </p>
                )}
                {form.clientId && (
                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        lines: [...prev.lines, emptyLine()],
                      }))
                    }
                    className="mt-1.5 inline-flex items-center gap-1 text-caption font-semibold text-accent hover:underline"
                  >
                    <Split className="h-3.5 w-3.5" aria-hidden />
                    Split across agreements
                  </button>
                )}
              </div>
            )}
          </div>

          {multi && (
            <ScheduleLinesEditor
              lines={form.lines}
              agreementOptions={agreementOptions}
              total={amountNum}
              error={errors.lines}
              onChange={setLine}
              onAdd={() =>
                setForm((prev) => ({
                  ...prev,
                  lines: [...prev.lines, emptyLine()],
                }))
              }
              onRemove={(i) =>
                setForm((prev) => {
                  const lines = prev.lines.filter((_, j) => j !== i);
                  // Back to one line: it keeps its amount and agreement; its
                  // label goes, because a one-line schedule prints the subject.
                  return {
                    ...prev,
                    lines: lines.length === 1 ? [{ ...lines[0], description: '' }] : lines,
                  };
                })
              }
            />
          )}

          <div>
            <Input
              label="Invoice subject"
              value={form.subject}
              onChange={(e) => set('subject', e.target.value)}
              error={errors.subject}
              placeholder="Support Retainer — %invoice_issue_month_name% %invoice_issue_year%"
              maxLength={200}
            />
            {/* What the client will actually read, resolved by the server —
                the same resolver that writes the invoice. */}
            {form.subject.trim() && (
              <p className="mt-1.5 text-caption text-grey-600">
                Next invoice reads{' '}
                <span className="font-semibold text-secondary">
                  “{preview.data?.subject ?? form.subject}”
                </span>
              </p>
            )}
            {unknown.length > 0 && (
              <p className="mt-1 text-caption text-status-late">
                Unknown variable{unknown.length > 1 ? 's' : ''}: {unknown.join(', ')} — left as
                typed on the invoice.
              </p>
            )}
            <VariableHelp onInsert={(token) => set('subject', `${form.subject}${token}`)} />
          </div>

          <div className={cn('grid gap-3', multi ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3')}>
            {!multi && (
              <Input
                label="Amount"
                type="number"
                inputMode="decimal"
                min="0.01"
                step="0.01"
                value={form.lines[0].amount}
                onChange={(e) => setLine(0, { amount: e.target.value })}
                error={errors.lines}
                hint={
                  Number.isFinite(amountNum) && amountNum > 0 ? formatMoney(amountNum) : undefined
                }
              />
            )}
            <Input
              label="Next invoice"
              type="date"
              value={form.nextIssueDate}
              onChange={(e) => set('nextIssueDate', e.target.value)}
              error={errors.nextIssueDate}
              hint={
                form.nextIssueDate <= today ? 'Due now — generate it whenever you like' : undefined
              }
            />
            <Select
              label="Repeats"
              options={CADENCE_OPTIONS}
              value={form.cadence}
              onChange={(e) => set('cadence', e.target.value as Cadence)}
            />
          </div>

          {/* Jesse, 2026-10-01: recurring invoices may go out without a human.
              Off by default, per schedule. 2026-11-02: on a Stripe-paid schedule
              the switch sends the invoice ON ITS DATE (a client's A/P wants it on
              the 1st) with no way to pay it by hand — the subscription pays it,
              and the charge settles that same invoice. See backend autopay.ts. */}
          <div
            className={cn(
              'flex items-center gap-2 rounded-lg border px-3 py-2',
              form.autoSend ? 'border-accent/40 bg-accent/5' : 'border-grey-200 bg-grey-50',
            )}
          >
            <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5">
              <input
                type="checkbox"
                checked={form.autoSend}
                onChange={(e) => setForm((prev) => ({ ...prev, autoSend: e.target.checked }))}
                className="h-4 w-4 shrink-0 cursor-pointer rounded border-grey-300 accent-accent"
              />
              <span className="min-w-0 text-body-sm">
                <span className="font-semibold text-secondary">Send automatically</span>
                <span className="text-grey-600"> · emailed on its date at 9:00 AM</span>
              </span>
            </label>
            <InfoHint label="Send automatically" className="shrink-0" align="end">
              {template?.stripe
                ? 'Paid by a Stripe subscription: on the invoice date at 9:00 AM the invoice is emailed to the billing contacts, saying it is paid automatically (no Pay button). When the charge clears it is marked paid and the receipt goes out. A card charged before 9:00 sends the paid invoice with its receipt instead. Off: each charge creates the invoice, marks it paid and emails the receipt.'
                : 'On the invoice date at 9:00 AM, the invoice is emailed to the client’s billing contacts — no draft to send. If it can’t go, it waits as a draft and this schedule says why. Off: it waits as a draft for you to send.'}
            </InfoHint>
          </div>

          {/* Refinements. Everything above is the sheet's four columns; these
              only sharpen it, and every one of them already has an answer. */}
          <div className="rounded-lg border border-grey-200">
            <button
              type="button"
              aria-expanded={showDetail}
              onClick={() => setShowDetail((v) => !v)}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-caption font-bold uppercase tracking-wider text-grey-500"
            >
              Optional detail
              <ChevronDown
                className={cn('h-4 w-4 transition-transform', showDetail && 'rotate-180')}
                aria-hidden
              />
            </button>
            {showDetail && (
              <div className="space-y-3 border-t border-grey-200 p-3">
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    label="Kind"
                    options={KIND_OPTIONS}
                    value={form.kind}
                    onChange={(e) => set('kind', e.target.value)}
                  />
                  <Input
                    label="Payment terms (days)"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    max="365"
                    step="1"
                    value={form.netDays}
                    onChange={(e) => set('netDays', e.target.value)}
                  />
                </div>
                <Textarea
                  label="Internal note"
                  rows={2}
                  maxLength={500}
                  value={form.note}
                  onChange={(e) => set('note', e.target.value)}
                  placeholder="Never printed on the invoice"
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 pt-2">
            {template ? (
              <Button
                type="button"
                variant="ghost"
                className="text-primary-600 hover:bg-primary-50"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={pending}>
                {template ? 'Save changes' : 'Create schedule'}
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this schedule?"
        description={
          template
            ? `${template.client.name} · ${formatMoney(template.amount)} ${template.cadenceLabel}. Invoices it already created are untouched. Pausing it keeps the history and can be undone.`
            : undefined
        }
        confirmLabel="Delete schedule"
        destructive
        onConfirm={() =>
          remove.mutateAsync(template!.id).then(
            () => {
              toast.success('Schedule deleted');
              setConfirmDelete(false);
              onOpenChange(false);
            },
            (error: unknown) => {
              toast.error(error instanceof Error ? error.message : 'Could not delete the schedule');
              throw error;
            },
          )
        }
      />

      <ConfirmDialog
        open={pastPlan !== null}
        onOpenChange={(o) => {
          if (!o) setPastPlan(null);
        }}
        title={`Also link the ${
          pastPlan ? pastPlan.result.linked.length + pastPlan.result.split.length : 0
        } invoices this schedule already made?`}
        description={pastPlan ? <PastPlanSummary result={pastPlan.result} /> : undefined}
        confirmLabel="Link them"
        onConfirm={() =>
          linkPast.mutateAsync({ id: pastPlan!.id, apply: true }).then(
            (r) => {
              toast.success(`${r.linked.length + r.split.length} past invoices linked`);
              setPastPlan(null);
            },
            (error: unknown) => {
              toast.error(
                error instanceof Error ? error.message : 'Could not link the past invoices',
              );
              throw error;
            },
          )
        }
      />
    </>
  );
}

function PastPlanSummary({ result }: { result: LinkPastResult }) {
  const list = (numbers: string[]) => numbers.map((n) => `#${n}`).join(', ');
  return (
    <span className="block space-y-1.5">
      {result.linked.length > 0 && (
        <span className="block">
          Credited to the schedule’s agreement: <span className="num">{list(result.linked)}</span>.
        </span>
      )}
      {result.split.length > 0 && (
        <span className="block">
          Split into this schedule’s lines, same total:{' '}
          <span className="num">{list(result.split)}</span>. Their PDF shows the lines from now on;
          amounts and payments do not change.
        </span>
      )}
      {result.skipped.length > 0 && (
        <span className="block text-grey-500">
          Left alone — link on the invoice:{' '}
          {result.skipped.map((s) => `#${s.number} (${s.reason})`).join(', ')}.
        </span>
      )}
      <span className="block text-grey-500">Only lines with no agreement are touched.</span>
    </span>
  );
}

/**
 * A schedule split across agreements (Jesse, 2026-11-04): "Hosting + WordPress
 * Updates" is $900 on the hosting agreement and $600 on the updates agreement,
 * billed as ONE invoice. Each row: what the line says, which agreement it
 * credits, how much.
 */
function ScheduleLinesEditor({
  lines,
  agreementOptions,
  total,
  error,
  onChange,
  onAdd,
  onRemove,
}: {
  lines: LineState[];
  agreementOptions: Array<{ value: string; label: string }>;
  total: number;
  error?: string;
  onChange: (i: number, patch: Partial<LineState>) => void;
  onAdd: () => void;
  onRemove: (i: number) => void;
}) {
  return (
    <fieldset className="rounded-lg border border-grey-200 p-3">
      <legend className="px-1 text-body-sm font-semibold text-secondary">
        Lines · one invoice
      </legend>
      <div className="space-y-3">
        {lines.map((line, i) => (
          <div
            key={i}
            className="grid grid-cols-[1fr_7.5rem_auto] items-end gap-2 sm:grid-cols-[1fr_1.4fr_7rem_auto]"
          >
            <Input
              label={i === 0 ? 'Line' : undefined}
              aria-label={`Line ${i + 1} label`}
              value={line.description}
              onChange={(e) => onChange(i, { description: e.target.value })}
              placeholder={i === 0 ? 'e.g. Hosting' : 'e.g. WordPress updates'}
              maxLength={200}
            />
            <div className="order-last col-span-3 sm:order-none sm:col-span-1">
              <Select
                label={i === 0 ? 'Agreement' : undefined}
                aria-label={`Line ${i + 1} agreement`}
                options={agreementOptions}
                value={line.agreementId}
                onChange={(e) => onChange(i, { agreementId: e.target.value })}
              />
            </div>
            <Input
              label={i === 0 ? 'Amount' : undefined}
              aria-label={`Line ${i + 1} amount`}
              type="number"
              inputMode="decimal"
              min="0.01"
              step="0.01"
              value={line.amount}
              onChange={(e) => onChange(i, { amount: e.target.value })}
              className="num text-right"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={`Remove line ${i + 1}`}
              onClick={() => onRemove(i)}
              className="mb-0.5"
            >
              <X className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        ))}
      </div>
      {error && (
        <p className="mt-2 text-caption text-status-late" role="alert">
          {error}
        </p>
      )}
      <div className="mt-3 flex items-center justify-between gap-2 border-t border-grey-100 pt-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onAdd}
          disabled={lines.length >= 10}
        >
          <Plus className="h-4 w-4" aria-hidden />
          Add line
        </Button>
        <span className="text-body-sm text-grey-600">
          Total <span className="num font-semibold text-secondary">{formatMoney(total)}</span>
        </span>
      </div>
      {lines.some((l) => !l.agreementId) && (
        <p className="mt-1.5 text-caption text-status-partial">
          A line with no agreement is not counted on any agreement.
        </p>
      )}
    </fieldset>
  );
}

/**
 * The Harvest variables, one click each. They are the reason a subject can be
 * pasted straight out of Harvest and keep working; hiding them behind
 * documentation would mean retyping the month into seventeen subjects every
 * month, which is the job this feature exists to delete.
 */
function VariableHelp({ onInsert }: { onInsert: (token: string) => void }) {
  const [open, setOpen] = useState(false);
  const common = [
    { token: '%invoice_issue_month_name%', label: 'September' },
    { token: '%invoice_issue_year%', label: '2026' },
    { token: '%invoice_issue_month%', label: '09' },
    { token: '%invoice_issue_quarter%', label: '3' },
    { token: '%invoice_issue_previous_month_name%', label: 'August' },
    { token: '%invoice_issue_next_month_name%', label: 'October' },
  ];
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-caption font-semibold text-accent-700 hover:underline"
      >
        {open ? 'Hide date variables' : 'Use a date variable'}
      </button>
      {open && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {common.map((v) => (
            <button
              key={v.token}
              type="button"
              onClick={() => onInsert(v.token)}
              title={`Inserts “${v.label}” when the invoice is issued`}
              className="rounded-full border border-grey-200 bg-grey-50 px-2 py-0.5 text-caption text-grey-700 hover:border-accent hover:text-accent-700"
            >
              {v.token}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
